import React, { useState, useRef, useEffect } from "react";
import {
  Bell,
  Check,
  CheckCheck,
  Archive,
  Settings,
  X,
  RefreshCw,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  useNotifications,
  NotificationType,
  NotificationPriority,
} from "../contexts/NotificationContext";
import { useOrganization } from "../context/OrganizationContext";

interface NotificationCenterProps {
  className?: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  className = "",
}) => {
  const navigate = useNavigate();
  const { selectedOrg } = useOrganization();
  const {
    notifications,
    unreadCount,
    isConnected,
    connectionStable,
    hasLoadedOnce,
    fetchNotifications,
    markAsRead,
    markAsClicked,
    archiveOldNotifications,
    refreshUnreadCount,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "all" | "mentions" | "assignments" | "updates"
  >("all");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Filter notifications by tab
  const filteredNotifications = notifications.filter((notification) => {
    if (activeTab === "all") return true;

    const mentionTypes = [
      NotificationType.MENTION_IN_TASK,
      NotificationType.MENTION_IN_COMMENT,
      NotificationType.MENTION_IN_OKR,
      NotificationType.MENTION_IN_NARRATIVE,
      NotificationType.MENTION_IN_PROPOSITION,
      NotificationType.MENTION_IN_ACCOUNT_PLAN,
    ];

    const assignmentTypes = [
      NotificationType.ASSIGNED_TO_TASK,
      NotificationType.ASSIGNED_TO_OKR,
      NotificationType.ASSIGNED_AS_OWNER,
      NotificationType.ADDED_AS_CO_OWNER,
    ];

    const updateTypes = [
      NotificationType.OWNED_ENTITY_UPDATED,
      NotificationType.OWNED_ENTITY_COMMENTED,
      NotificationType.OWNED_ENTITY_STATUS_CHANGED,
      NotificationType.TASK_BLOCKED,
      NotificationType.TASK_UNBLOCKED,
      NotificationType.KEY_RESULT_PROGRESS_UPDATE,
      NotificationType.OKR_SCORE_CHANGED,
      NotificationType.COMPANY_STATUS_CHANGED,
      NotificationType.POSITION_SCORE_CHANGED,
    ];

    switch (activeTab) {
      case "mentions":
        return mentionTypes.includes(notification.type);
      case "assignments":
        return assignmentTypes.includes(notification.type);
      case "updates":
        return updateTypes.includes(notification.type);
      default:
        return true;
    }
  });

  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce(
    (groups, notification) => {
      const date = new Date(notification.created_at);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let groupLabel: string;
      if (date.toDateString() === today.toDateString()) {
        groupLabel = "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        groupLabel = "Yesterday";
      } else {
        groupLabel = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }

      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(notification);
      return groups;
    },
    {} as Record<string, typeof notifications>,
  );

  // Get notification icon based on type
  const getNotificationIcon = (type: NotificationType) => {
    if (type.includes("mention")) return "@";
    if (type.includes("assigned")) return "👤";
    if (type.includes("blocked")) return "🚫";
    if (type.includes("deadline")) return "⏰";
    if (type.includes("status")) return "📊";
    return "📢";
  };

  // Get priority color
  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.HIGH:
        return "text-red-600 dark:text-red-400";
      case NotificationPriority.MEDIUM:
        return "text-yellow-600 dark:text-yellow-400";
      case NotificationPriority.LOW:
        return "text-gray-600 dark:text-gray-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  // Handle notification click
  const handleNotificationClick = async (
    notification: (typeof notifications)[0],
  ) => {
    await markAsClicked(notification.id);

    // Navigate to the entity
    // First check if entity_url is provided in notification data
    const entityUrl = notification.data?.entity_url;
    if (entityUrl) {
      navigate(entityUrl);
      setIsOpen(false);
      return;
    }

    // Fallback to constructing URL from entity type and ID
    if (notification.entity_type && notification.entity_id) {
      const entityRoutes: Record<string, string> = {
        task: `/gravity-plans?taskId=${notification.entity_id}`,
        okr: `/okrs?okr=${notification.entity_id}`,
        company: `/companies/${notification.entity_id}`,
        proposition: `/propositions/${notification.entity_id}`,
        segment: `/segments/${notification.entity_id}`,
        position: `/positions/${notification.entity_id}`,
        account_plan: `/companies?account_plan=${notification.entity_id}`,
        narrative: `/narratives/${notification.entity_id}`,
        contact: `/companies?contact=${notification.entity_id}`,
        hypothesis: `/companies?hypothesis=${notification.entity_id}`,
      };

      const route = entityRoutes[notification.entity_type];
      if (route) {
        navigate(route);
        setIsOpen(false);
      }
    }
  };

  // Fetch notifications when dropdown opens
  const handleToggleDropdown = async () => {
    if (!isOpen) {
      // Fetch notifications when opening - this already includes the unread count
      await fetchNotifications();
      // No need to call refreshUnreadCount - fetchNotifications already sets the count
    }
    setIsOpen(!isOpen);
  };

  // Determine tooltip text based on connection state
  const getConnectionTooltip = () => {
    if (connectionStable) {
      return "Real-time notifications active";
    } else if (isConnected && !connectionStable) {
      return "Establishing connection...";
    } else if (!isConnected && hasLoadedOnce) {
      return "Notifications may be delayed";
    }
    return "Notifications";
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={handleToggleDropdown}
        className="relative p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors"
        aria-label="Notifications"
        title={getConnectionTooltip()}
      >
        <Bell className="h-6 w-6" />
        {/* Only show unread count badge if we've successfully loaded data AND have unread notifications */}
        {hasLoadedOnce && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
        {/* Connection indicators temporarily disabled while fixing Cloud SQL issue
        {connectionStable && (
          <span
            className="absolute bottom-1 right-1 h-2 w-2 bg-green-500 rounded-full animate-pulse"
            title="Real-time updates active"
          />
        )}
        {isConnected && !connectionStable && (
          <span
            className="absolute bottom-1 right-1 h-2 w-2 bg-yellow-500 rounded-full"
            title="Connecting..."
          />
        )}
        */}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Notifications
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={async () => {
                    await fetchNotifications();
                    // fetchNotifications already updates the count
                  }}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Refresh notifications"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  onClick={() => markAsRead(undefined, true)}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Mark all as read"
                >
                  <CheckCheck className="h-4 w-4" />
                </button>
                <button
                  onClick={() => archiveOldNotifications()}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Archive old notifications"
                >
                  <Archive className="h-4 w-4" />
                </button>
                <button
                  onClick={() => navigate(`/organization?tab=notifications`)}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Notification settings"
                >
                  <Settings className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1">
              {(["all", "mentions", "assignments", "updates"] as const).map(
                (tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                      activeTab === tab
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                        : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ),
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[400px]">
            {Object.keys(groupedNotifications).length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No notifications</p>
              </div>
            ) : (
              Object.entries(groupedNotifications).map(
                ([groupLabel, groupNotifications]) => (
                  <div key={groupLabel}>
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 text-xs font-medium text-gray-500 dark:text-gray-400">
                      {groupLabel}
                    </div>
                    {groupNotifications.map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 ${
                          !notification.read_at
                            ? "bg-blue-50 dark:bg-blue-900/20"
                            : ""
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-lg">
                            {getNotificationIcon(notification.type)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p
                                  className={`text-sm font-medium ${getPriorityColor(notification.priority)}`}
                                >
                                  {notification.title}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  {notification.message}
                                </p>
                                {notification.actor_name && (
                                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    by {notification.actor_name}
                                  </p>
                                )}
                              </div>
                              <div className="ml-2 flex-shrink-0">
                                {!notification.read_at && (
                                  <span className="inline-block h-2 w-2 bg-blue-500 rounded-full" />
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-500 dark:text-gray-500">
                                {formatDistanceToNow(
                                  new Date(notification.created_at),
                                  {
                                    addSuffix: true,
                                  },
                                )}
                              </span>
                              {notification.group_count > 1 && (
                                <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                                  +{notification.group_count - 1} similar
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ),
              )
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                navigate(`/notifications`);
                setIsOpen(false);
              }}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
